import { isSpamContact, UPLIFT_SPAM_BLOCKED_EVENT } from '@/config/spamList';
import {
  UPLIFT_API_BASE,
  UPLIFT_ENDPOINTS,
  UPLIFT_LEAD_FLUSH_DELAY_MS,
  UPLIFT_TRACKER_API,
} from '@/config/uplift.config';
import type {
  UpliftInquiry,
  UpliftLead,
  UpliftSearchContext,
  UpliftSearchLeg,
  UpliftTrackerPayload,
  UpliftTrackerResponse,
  UpliftTripType,
} from '@/types/uplift.types';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

/**
 * The Uplift widget is an Angular custom element that renders inside our page and
 * talks to its own backend over XMLHttpRequest. We wrap XHR so every successful
 * submission is mirrored into Pipedrive. A contact on the spam list never leaves
 * the browser: the request is aborted and the widget is told to stop.
 *
 * Sequence the widget produces on a first search (contact gate is enabled):
 *   GET  widget/trips/mock      placeholder cards behind the gate
 *   POST widget/lead            contact details      <- captured
 *   GET  widget/trips/oneway    the actual search    <- route context
 * and, when a user inquires about a specific card:
 *   POST widget/lead            contact details again
 *   POST widget/inquire         the chosen trip card <- captured
 */

const TRACKER_FLAG = '__heraUpliftTrackerInstalled';
const DEAL_STORAGE_KEY = 'hera_uplift_deal_id';
const LOG_PREFIX = '[upliftTracker]';

type XhrMeta = { method: string; url: string };
type UpliftEvent = 'lead' | 'inquire' | 'search' | 'locations';

const xhrMeta = new WeakMap<XMLHttpRequest, XhrMeta>();

/** Airport/city id -> readable name, filled from autocomplete responses */
const locationNames = new Map<number, string>();

let pendingLead: UpliftLead | null = null;
let lastLead: UpliftLead | null = null;
let lastSearch: UpliftSearchContext | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let widgetGuid: string | undefined;
let spamBlocked = false;

/**
 * Submissions are sent one after another. The first one creates the deal, and
 * every later one needs the id it returned, otherwise a visit ends up as two deals.
 */
let sendChain: Promise<void> = Promise.resolve();

function enqueueSend(
  lead: UpliftLead,
  search?: UpliftSearchContext,
  inquiry?: UpliftInquiry
) {
  sendChain = sendChain.then(() => send(lead, search, inquiry));
}

export function setUpliftWidgetGuid(guid: string) {
  widgetGuid = guid;
}

/* ------------------------------------------------------------------ helpers */

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function resolveName(id?: number): string | undefined {
  if (id === undefined) return undefined;
  return locationNames.get(id);
}

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw, window.location.href);
  } catch {
    return null;
  }
}

function readResponseJson(xhr: XMLHttpRequest): unknown {
  try {
    if (xhr.responseType === 'json') return xhr.response;
    if (xhr.responseType === '' || xhr.responseType === 'text') {
      return xhr.responseText ? JSON.parse(xhr.responseText) : null;
    }
  } catch {
    /* a body we cannot parse is simply not tracked */
  }
  return null;
}

function readRequestJson(body: unknown): Record<string, unknown> | null {
  if (typeof body !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(body);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function isUpliftApi(url: URL): boolean {
  return (
    url.href.startsWith(UPLIFT_API_BASE) || url.hostname === 'app.uplift.aero'
  );
}

function contactFromBody(
  body: unknown
): { email?: string; phone?: string } | null {
  const json = readRequestJson(body);
  if (json) {
    const nested =
      json.lead && typeof json.lead === 'object'
        ? (json.lead as Record<string, unknown>)
        : null;
    return {
      email: toText(json.email) ?? (nested ? toText(nested.email) : undefined),
      phone: toText(json.phone) ?? (nested ? toText(nested.phone) : undefined),
    };
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const email = body.get('email');
    const phone = body.get('phone');
    return {
      email: typeof email === 'string' ? toText(email) : undefined,
      phone: typeof phone === 'string' ? toText(phone) : undefined,
    };
  }

  return null;
}

/** Stop this request and, the first time, tell the widget to show the notice. */
function rejectSpamRequest(xhr: XMLHttpRequest) {
  const firstBlock = !spamBlocked;
  spamBlocked = true;

  if (firstBlock) {
    window.dispatchEvent(new CustomEvent(UPLIFT_SPAM_BLOCKED_EVENT));
  }

  queueMicrotask(() => {
    try {
      xhr.abort();
    } catch {
      /* the widget already moved on */
    }
  });
}

/** Uplift wraps every response as `{ data: ..., code: 200 }` */
function unwrapData(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = (payload as { data?: unknown }).data;
  return data && typeof data === 'object' && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : null;
}

/* ---------------------------------------------------------------- classify */

function classify(meta: XhrMeta, url: URL): UpliftEvent | null {
  const path = url.pathname;
  const isPost = meta.method === 'POST';

  if (isPost && path.endsWith(UPLIFT_ENDPOINTS.lead)) return 'lead';
  if (isPost && path.endsWith(UPLIFT_ENDPOINTS.inquire)) return 'inquire';
  if (path.endsWith(UPLIFT_ENDPOINTS.mockTrips)) return null;
  if (path.includes(UPLIFT_ENDPOINTS.trips)) return 'search';
  if (
    path.endsWith(UPLIFT_ENDPOINTS.locations) ||
    path.endsWith(UPLIFT_ENDPOINTS.cities) ||
    path.endsWith(UPLIFT_ENDPOINTS.airports)
  ) {
    return 'locations';
  }
  return null;
}

function tripTypeFromPath(path: string): UpliftTripType | undefined {
  if (path.endsWith('/oneway')) return 'oneWay';
  if (path.endsWith('/roundtrip')) return 'roundTrip';
  if (path.endsWith('/multileg')) return 'multiLeg';
  return undefined;
}

/* ----------------------------------------------------------------- handlers */

/** Remember every id the autocomplete returned, so searches can be named later */
function collectLocations(payload: unknown) {
  const data = unwrapData(payload);
  if (!data) return;

  for (const bucket of ['locations', 'cities', 'airports'] as const) {
    const list = data[bucket];
    if (!Array.isArray(list)) continue;

    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue;
      const item = entry as Record<string, unknown>;
      const id = toNumber(item.id ?? item.cityId ?? item.airportId);
      const name = toText(item.name) ?? toText(item.fullname);
      if (id !== undefined && name) locationNames.set(id, name);
    }
  }
}

function buildSearchFromQuery(
  url: URL,
  tripType?: UpliftTripType
): UpliftSearchContext {
  const params = url.searchParams;
  const originAirportId = toNumber(params.get('originAirportId'));
  const destinationAirportId = toNumber(params.get('destinationAirportId'));

  return {
    tripType,
    originAirportId,
    destinationAirportId,
    originName: resolveName(originAirportId),
    destinationName: resolveName(destinationAirportId),
    departureDate: toText(params.get('departureDate')),
    returnDate: toText(params.get('returnDate')),
    passengers: toNumber(params.get('passengers')),
  };
}

function buildSearchFromLegs(
  body: Record<string, unknown>
): UpliftSearchContext | null {
  const rawLegs = body.legs;
  if (!Array.isArray(rawLegs) || rawLegs.length === 0) return null;

  const legs: UpliftSearchLeg[] = [];
  let originAirportId: number | undefined;
  let destinationAirportId: number | undefined;
  let departureDate: string | undefined;

  rawLegs.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    const leg = entry as Record<string, unknown>;
    const from = toNumber(leg.originAirportId);
    const to = toNumber(leg.destinationAirportId);
    const date = toText(leg.date);

    if (index === 0) {
      originAirportId = from;
      departureDate = date;
    }
    if (to !== undefined) destinationAirportId = to;

    legs.push({
      originName: resolveName(from),
      destinationName: resolveName(to),
      date,
    });
  });

  return {
    tripType: 'multiLeg',
    originAirportId,
    destinationAirportId,
    originName: resolveName(originAirportId),
    destinationName: resolveName(destinationAirportId),
    departureDate,
    legs,
  };
}

function handleSearch(url: URL, requestBody: unknown) {
  const tripType = tripTypeFromPath(url.pathname);
  const body = readRequestJson(requestBody);
  const search = body?.legs
    ? buildSearchFromLegs(body)
    : buildSearchFromQuery(url, tripType);

  if (!search) return;
  lastSearch = search;

  // The lead is posted just before the search, so this is the moment it is complete
  if (pendingLead) flushLead();
}

function handleLead(requestBody: unknown) {
  const body = readRequestJson(requestBody);
  if (!body) return;

  const lead: UpliftLead = {
    firstName: toText(body.firstName),
    lastName: toText(body.lastName),
    email: toText(body.email),
    phone: toText(body.phone),
    smsConsent: body.smsConsent === true,
    alertEnabled: body.alertEnabled === true,
  };

  if (!lead.email && !lead.phone) return;

  pendingLead = lead;
  lastLead = lead;

  // Give the search request that follows the gate a chance to arrive first
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushLead, UPLIFT_LEAD_FLUSH_DELAY_MS);
}

function handleInquire(requestBody: unknown) {
  const body = readRequestJson(requestBody);
  if (!body) return;

  const inquiry: UpliftInquiry = {
    tripType: toText(body.tripType) as UpliftTripType | undefined,
    notes: toText(body.notes),
    passengers: toNumber(body.passengers),
    fromDate: toText(body.fromDate),
    toDate: toText(body.toDate),
    tripCard: (body.tripCard ?? undefined) as UpliftInquiry['tripCard'],
  };

  const lead = pendingLead ?? lastLead;
  if (!lead) return;

  pendingLead = null;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  enqueueSend(lead, lastSearch ?? undefined, inquiry);
}

/* --------------------------------------------------------------- forwarding */

function readDealId(): number | undefined {
  try {
    const stored = window.sessionStorage.getItem(DEAL_STORAGE_KEY);
    return stored ? toNumber(stored) : undefined;
  } catch {
    return undefined;
  }
}

function storeDealId(dealId: number) {
  try {
    window.sessionStorage.setItem(DEAL_STORAGE_KEY, String(dealId));
  } catch {
    /* private mode, keep going without deduplication */
  }
}

function buildPayload(
  lead: UpliftLead,
  search?: UpliftSearchContext,
  inquiry?: UpliftInquiry
): UpliftTrackerPayload {
  const dealId = readDealId();

  return {
    lead,
    ...(search ? { search } : {}),
    ...(inquiry ? { inquiry } : {}),
    ...(dealId ? { dealId } : {}),
    widgetGuid,
    pageUrl: window.location.href,
    referrer: document.referrer || undefined,
    ...getAttributionPayload(),
  };
}

function flushLead() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const lead = pendingLead;
  if (!lead) return;
  pendingLead = null;

  enqueueSend(lead, lastSearch ?? undefined);
}

async function send(
  lead: UpliftLead,
  search?: UpliftSearchContext,
  inquiry?: UpliftInquiry
) {
  const payload = buildPayload(lead, search, inquiry);

  try {
    const response = await fetch(UPLIFT_TRACKER_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) {
      console.error(LOG_PREFIX, 'forwarding failed:', await response.text());
      return;
    }

    const result = (await response.json()) as UpliftTrackerResponse;
    if (result.dealId) storeDealId(result.dealId);
  } catch (error) {
    console.error(LOG_PREFIX, 'network error:', error);
  }
}

/** Last chance to keep a captured contact if the visitor leaves mid-flow */
function handlePageHide() {
  const lead = pendingLead;
  if (!lead) return;
  pendingLead = null;

  const payload = buildPayload(lead, lastSearch ?? undefined);
  try {
    const blob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    navigator.sendBeacon(UPLIFT_TRACKER_API, blob);
  } catch {
    /* nothing else we can do while the page is going away */
  }
}

/* ---------------------------------------------------------------- installer */

/**
 * Wrap XMLHttpRequest once. Must run before the widget script is inserted,
 * otherwise the first requests are made through the untouched prototype.
 */
export function installUpliftTracker() {
  if (typeof window === 'undefined') return;

  const flagged = window as unknown as Record<string, boolean>;
  if (flagged[TRACKER_FLAG]) return;
  flagged[TRACKER_FLAG] = true;

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    xhrMeta.set(this, {
      method: String(method).toUpperCase(),
      url: String(url),
    });
    return (
      originalOpen as unknown as (
        this: XMLHttpRequest,
        ...args: unknown[]
      ) => void
    ).apply(this, [method, url, ...rest]);
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null
  ) {
    const meta = xhrMeta.get(this);
    const url = meta ? parseUrl(meta.url) : null;
    const event = meta && url ? classify(meta, url) : null;

    if (url && isUpliftApi(url)) {
      if (spamBlocked) {
        rejectSpamRequest(this);
        return;
      }

      if (event === 'lead' || event === 'inquire') {
        const contact = contactFromBody(body);
        if (contact && isSpamContact(contact.email, contact.phone)) {
          rejectSpamRequest(this);
          return;
        }
      }
    }

    if (event && url) {
      this.addEventListener('load', () => {
        // Only mirror what Uplift itself accepted
        if (this.status < 200 || this.status >= 300) return;

        try {
          if (event === 'locations') collectLocations(readResponseJson(this));
          else if (event === 'lead') handleLead(body);
          else if (event === 'inquire') handleInquire(body);
          else if (event === 'search') handleSearch(url, body);
        } catch (error) {
          // Never let our tracking break the widget
          console.error(LOG_PREFIX, 'capture failed:', error);
        }
      });
    }

    return originalSend.call(this, body);
  };

  window.addEventListener('pagehide', handlePageHide);
}
